// NodeAssertionTooltip — HTML overlay tooltip that appears on node hover,
// showing per-node assertion details, schema contracts, boundary crossings,
// and the event type that was validated.
//
// Hover persistence: reads tooltipNodeId from Redux, which is set on
// hoverNode but NOT cleared on clearHover. Only the tooltip's own
// onMouseLeave dispatches clearTooltip. This keeps the tooltip alive
// while the mouse travels from SVG node to the HTML tooltip div.

import { useMemo, useCallback, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectActualPath,
  selectSimulationStatus,
  selectEffectiveAssertionResults,
  type AssertionResult,
} from '../../store/slices/simulationSlice.ts';
import { selectTooltipNodeId, clearTooltip } from '../../store/slices/uiSlice.ts';
import { selectEvents } from '../../store/slices/eventsSlice.ts';
import {
  selectNodeById,
  selectConnectionsForNode,
  selectLaneById,
} from '../../store/slices/topologySlice.ts';
import type { RootState } from '../../store/index.ts';
import type { NodePosition } from './layoutEngine.ts';

const TIER_LABELS: Record<string, string> = {
  path: 'Path',
  boundary_crossing: 'Boundary',
  schema_contract: 'Schema',
  branch_evaluation: 'Branch',
  terminal_state: 'Terminal',
};

const TIER_ICONS: Record<string, string> = {
  path: '◈',
  boundary_crossing: '⬡',
  schema_contract: '⬢',
  branch_evaluation: '◇',
  terminal_state: '◉',
};

const TOOLTIP_MAX_W = 360;
const TOOLTIP_MAX_H = 420;

export default function NodeAssertionTooltip({
  nodePositions,
  scale,
  pan,
  containerSize,
  offsetLeft = 0,
}: {
  nodePositions: Record<string, NodePosition>;
  scale: number;
  pan: { x: number; y: number };
  containerSize: { w: number; h: number };
  offsetLeft?: number;
}) {
  const dispatch = useDispatch();
  const activeId = useSelector(selectTooltipNodeId);
  const simulationStatus = useSelector(selectSimulationStatus);
  const actualPath = useSelector(selectActualPath);
  const reverseNodeMap = useSelector(
    (state: RootState) => state.topology.topologyToScenarioNodeMap,
  );
  const assertionResults = useSelector(selectEffectiveAssertionResults);
  const events = useSelector(selectEvents);

  const handleTooltipLeave = useCallback(() => {
    dispatch(clearTooltip());
  }, [dispatch]);

  const stopPropagation = useCallback((e: React.PointerEvent) => {
    e.stopPropagation();
  }, []);
  const stopWheelPropagation = useCallback((e: React.WheelEvent) => {
    e.stopPropagation();
  }, []);

  const node = useSelector((state: RootState) =>
    activeId ? selectNodeById(state, activeId) : undefined,
  );
  const connections = useSelector((state: RootState) =>
    activeId ? selectConnectionsForNode(state, activeId) : [],
  );
  const lane = useSelector((state: RootState) =>
    node ? selectLaneById(state, node.lane) : undefined,
  );

  const nodeData = useMemo(() => {
    if (!activeId || !node) return null;

    // Match across namespaces: actualPath contains scenario IDs,
    // activeId is a topology ID. Check both directly and via reverse map.
    const scenarioIdForActive = reverseNodeMap[activeId] ?? null;
    const stepIndices = actualPath
      .map((nid, i) =>
        nid === activeId || (scenarioIdForActive && nid === scenarioIdForActive) ? i : -1,
      )
      .filter((i) => i >= 0);

    const nodeAssertions: AssertionResult[] = [];
    for (const stepIdx of stepIndices) {
      for (const r of assertionResults) {
        const matches =
          r.assertionId === `path-step-${stepIdx}` ||
          r.assertionId.startsWith(`boundary-${stepIdx}-`) ||
          r.assertionId.startsWith(`schema-${stepIdx}-`) ||
          r.assertionId.startsWith(`branch-eval-${stepIdx}-`) ||
          r.assertionId.startsWith(`terminal-${stepIdx}-`);
        if (matches) nodeAssertions.push(r);
      }
    }

    const byTier: Record<string, AssertionResult[]> = {};
    for (const a of nodeAssertions) {
      if (!byTier[a.assertionType]) byTier[a.assertionType] = [];
      byTier[a.assertionType].push(a);
    }

    const firstStepIdx = stepIndices[0];
    const event = firstStepIdx != null ? events[firstStepIdx] : null;

    const inboundContracts = connections
      .filter((c) => c.to === activeId && c.schemaContract)
      .map((c) => c.schemaContract!);

    const boundaryConnections = connections.filter(
      (c) => (c.to === activeId || c.from === activeId) && c.crossBoundary,
    );

    return {
      node,
      stepIndices,
      nodeAssertions,
      byTier,
      event,
      inboundContracts,
      boundaryConnections,
      lane,
    };
  }, [activeId, node, actualPath, reverseNodeMap, assertionResults, events, connections, lane]);

  // Dismiss tooltip when tapping outside (critical for touch devices where
  // onMouseLeave never fires)
  const tooltipRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!activeId) return;
    const handlePointerDown = (e: PointerEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(e.target as Node)) {
        dispatch(clearTooltip());
      }
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [activeId, dispatch]);

  if (!activeId || !nodeData || simulationStatus === 'idle') return null;

  const pos = nodePositions[activeId];
  if (!pos) return null;

  const isVisited = nodeData.stepIndices.length > 0;
  // Skip-aware counts: a SKIP is a non-evaluation, never a failure. The ✗
  // count includes only genuine failures (!passed && !skipped).
  const passCount = nodeData.nodeAssertions.filter((a) => a.passed).length;
  const skipCount = nodeData.nodeAssertions.filter((a) => a.skipped === true).length;
  const failCount = nodeData.nodeAssertions.length - passCount - skipCount;

  const nodeRight = (pos.x + pos.w) * scale + pan.x + offsetLeft;
  const nodeTop = pos.y * scale + pan.y;
  const nodeLeft = pos.x * scale + pan.x + offsetLeft;

  const cW = containerSize.w || window.innerWidth;
  const cH = containerSize.h || window.innerHeight;

  let left = nodeRight + 10;
  let top = nodeTop;

  if (left + TOOLTIP_MAX_W > cW - 8) {
    left = nodeLeft - TOOLTIP_MAX_W - 10;
  }
  if (left < 8) left = 8;
  if (top + TOOLTIP_MAX_H > cH - 8) {
    top = Math.max(8, cH - TOOLTIP_MAX_H - 8);
  }

  return (
    <div
      ref={tooltipRef}
      className="node-tooltip"
      onMouseLeave={handleTooltipLeave}
      onPointerDown={stopPropagation}
      onPointerMove={stopPropagation}
      onPointerUp={stopPropagation}
      onWheel={stopWheelPropagation}
      style={{ left, top }}
    >
      <div className="node-tooltip-header">
        <span className="node-tooltip-id" title={activeId}>
          {activeId.replace(/^moment-\d+-/i, '')}
        </span>
        <span className="node-tooltip-type">{nodeData.node.type}</span>
        {nodeData.node.aggregate && (
          <span className="node-tooltip-aggregate">{nodeData.node.aggregate}</span>
        )}
      </div>

      {nodeData.node.detail && <div className="node-tooltip-detail">{nodeData.node.detail}</div>}

      {nodeData.lane && (
        <div className="node-tooltip-lane">
          <span
            className="node-tooltip-lane-dot"
            style={{ background: nodeData.lane.color ?? '#888' }}
          />
          {nodeData.lane.label}
        </div>
      )}

      {nodeData.event && (
        <div className="node-tooltip-event">
          <span className="node-tooltip-event-label">Event:</span>
          <span className="node-tooltip-event-type">{nodeData.event.eventType}</span>
        </div>
      )}

      {nodeData.inboundContracts.length > 0 && (
        <div className="node-tooltip-contracts">
          <span className="node-tooltip-section-label">Schema Contracts</span>
          {nodeData.inboundContracts.map((contract) => (
            <div key={contract.contractId} className="node-tooltip-contract">
              <span className="node-tooltip-contract-id">{contract.contractId}</span>
              <span className="node-tooltip-contract-fields">
                {contract.requiredFields.join(', ')}
              </span>
              {contract.fieldTypes && (
                <span className="node-tooltip-contract-types">
                  {Object.entries(contract.fieldTypes)
                    .map(([f, t]) => `${f}:${t}`)
                    .join(', ')}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {nodeData.boundaryConnections.length > 0 && (
        <div className="node-tooltip-boundaries">
          <span className="node-tooltip-section-label">Boundary Crossings</span>
          {nodeData.boundaryConnections.map((c, i) => (
            <div key={i} className="node-tooltip-boundary">
              {c.from} → {c.to}
              {c.relationship && (
                <span className="node-tooltip-relationship">{c.relationship}</span>
              )}
            </div>
          ))}
        </div>
      )}

      {isVisited && nodeData.nodeAssertions.length > 0 && (
        <div className="node-tooltip-assertions">
          <div className="node-tooltip-assertion-summary">
            <span className="node-tooltip-section-label">Assertions</span>
            <span className="node-tooltip-assertion-counts">
              <span className="node-tooltip-pass">{passCount}✓</span>
              {skipCount > 0 && <span className="node-tooltip-skip">{skipCount}—</span>}
              {failCount > 0 && <span className="node-tooltip-fail">{failCount}✗</span>}
            </span>
          </div>
          {Object.entries(nodeData.byTier).map(([tier, results]) => {
            const tierPassed = results.filter((r) => r.passed).length;
            const tierSkipped = results.filter((r) => r.skipped === true).length;
            const tierFailed = results.length - tierPassed - tierSkipped;
            const tierClass =
              tierFailed > 0
                ? 'node-tooltip-tier-fail'
                : tierPassed > 0
                  ? 'node-tooltip-tier-pass'
                  : 'node-tooltip-tier-skip';
            return (
              <div key={tier} className={`node-tooltip-tier ${tierClass}`}>
                <div className="node-tooltip-tier-header">
                  <span className="node-tooltip-tier-icon">{TIER_ICONS[tier] ?? '•'}</span>
                  <span className="node-tooltip-tier-name">{TIER_LABELS[tier] ?? tier}</span>
                  <span className="node-tooltip-tier-counts">
                    {tierPassed}✓{tierSkipped > 0 ? ` ${tierSkipped}—` : ''}
                    {tierFailed > 0 ? ` ${tierFailed}✗` : ''}
                  </span>
                </div>
                {results.map((r) => (
                  <div
                    key={r.assertionId}
                    className={`node-tooltip-result ${
                      r.passed
                        ? 'node-tooltip-result-pass'
                        : r.skipped
                          ? 'node-tooltip-result-skip'
                          : 'node-tooltip-result-fail'
                    }`}
                  >
                    <span className="node-tooltip-result-icon">
                      {r.passed ? '✓' : r.skipped ? '—' : '✗'}
                    </span>
                    <span className="node-tooltip-result-detail">{formatDetail(r.detail)}</span>
                    {!r.passed && !r.skipped && r.diagnosis && (
                      <span className="node-tooltip-diagnosis-hint" title={r.diagnosis.explanation}>
                        {r.diagnosis.summary}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {!isVisited && <div className="node-tooltip-unreached">Not exercised by this scenario</div>}

      {(nodeData.node.adrs?.length ?? 0) > 0 && (
        <div className="node-tooltip-adrs">
          {nodeData.node.adrs?.map((adr) => (
            <span key={adr} className="node-tooltip-adr">
              {adr}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function formatDetail(detail: string): string {
  return detail.replace(/^Step \d+:\s*/, '');
}
